import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject} from 'rxjs';
import {filter, first, map, switchMap} from 'rxjs/operators';
import {FilterService} from './filter.service';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {Uuid} from '../interface/response/uuid';
import {
  CommissionTable,
  CommissionTableListItem
} from '../interface/commission-table';
import {AuthService} from './auth.service';
import {UserPermissions} from '../interface/user-permissions';

@Injectable({
  providedIn: 'root'
})
export class CommissionTableService {

  public readonly commissionTableListSubject:
    ReplaySubject<CountedAndPaginated<CommissionTableListItem>>
    = new ReplaySubject<CountedAndPaginated<CommissionTableListItem>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    private auth: AuthService,
  ) {
  }

  public create(formData: any): Observable<Uuid> {
    return this.auth
      .getUserPermissions()
      .pipe(
        filter(
          (userPermissions: UserPermissions) =>
            userPermissions.commission_table.write
        ),
        first(),
        switchMap(() =>
          this.httpClient.post<Uuid>(
            '/rest/crm/commission-table',
            formData
          )
        )
      );
  }

  public update(
    uuid: string,
    formData: any
  ): Observable<CommissionTable> {
    return this.auth
      .getUserPermissions()
      .pipe(
        filter(
          (userPermissions: UserPermissions) =>
            userPermissions.commission_table.write
        ),
        first(),
        switchMap(() =>
          this.httpClient.put<CommissionTable>(
            `/rest/crm/commission-table/${uuid}`,
            formData
          )
        )
      );
  }

  public get(uuid: string): Observable<CommissionTable> {
    return this.auth
      .getUserPermissions()
      .pipe(
        filter(
          (userPermissions: UserPermissions) =>
            userPermissions.commission_table.read
        ),
        first(),
        switchMap(() =>
          this.httpClient.get<CommissionTable>(
            `/rest/crm/commission-table/${uuid}`
          )
        )
      );
  }

  public loadList(
    page: number,
    pageSize: number,
    filters: object
  ): void {
    this.auth
      .getUserPermissions()
      .pipe(
        filter(
          (userPermissions: UserPermissions) =>
            userPermissions.commission_table.delete
        ),
        first(),
        switchMap(() =>
          this.httpClient.get<CountedAndPaginated<CommissionTableListItem>>(
            `/rest/crm/commission-table?page=${page + 1}&pageSize=${pageSize}&`
            + this.filterService.parseFilters(filters)
          )
        )
      )
      .subscribe(
        (result: CountedAndPaginated<CommissionTableListItem>) => {
          this.commissionTableListSubject.next(result);
        }
      );
  }

  /**
   * Lista tabel prowizyjnych do selectów.
   */
  public getOptions(): Observable<CommissionTableListItem[]> {
    return this.auth
      .getUserPermissions()
      .pipe(
        filter(
          (userPermissions: UserPermissions) =>
            userPermissions.commission_table.read
        ),
        first(),
        switchMap(() =>
          this.httpClient.get<CountedAndPaginated<CommissionTableListItem>>(
            '/rest/crm/commission-table-options?page=1&pageSize=1000'
          )
        ),
        map(
          (result: CountedAndPaginated<CommissionTableListItem>) =>
            result.items
        )
      );
  }
}
